import type { InkPoint } from './types.js';

/**
 * Packed point storage (decision J3): x/y quantised to 0.1 mm, pressure to 0–255, optional dt in
 * whole milliseconds. Layout: flags (bit 0 = dt present), count, then per point the zigzag
 * varint deltas of x, y, pressure (+ dt as plain varint), all base64url encoded (no padding).
 * About 3–5 bytes per point (~1.5 KB per second of writing at 240 Hz).
 */

/** Quantisation step of positions in metres (0.1 mm). */
export const POSITION_STEP = 1e-4;
/** Pressure levels (0–255). */
export const PRESSURE_LEVELS = 255;

const FLAG_DT = 1;
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
const LOOKUP: ReadonlyMap<string, number> = new Map([...ALPHABET].map((c, i) => [c, i]));

export class InkCodecError extends Error {
  override name = 'InkCodecError';
}

/** Zigzag mapping of a (safe) integer to a non-negative integer, without 32-bit operators. */
export function zigzag(n: number): number {
  return n >= 0 ? n * 2 : -n * 2 - 1;
}

export function unzigzag(z: number): number {
  return z % 2 === 0 ? z / 2 : -(z + 1) / 2;
}

function writeVarint(out: number[], value: number): void {
  let v = value;
  while (v >= 128) {
    out.push((v % 128) + 128);
    v = Math.floor(v / 128);
  }
  out.push(v);
}

class Reader {
  private pos = 0;
  constructor(private readonly bytes: readonly number[]) {}

  varint(): number {
    let value = 0;
    let factor = 1;
    for (;;) {
      const b = this.bytes[this.pos++];
      if (b === undefined) throw new InkCodecError('Truncated point data');
      value += (b % 128) * factor;
      if (b < 128) return value;
      factor *= 128;
    }
  }

  get done(): boolean {
    return this.pos >= this.bytes.length;
  }
}

export function bytesToBase64Url(bytes: readonly number[]): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i]!;
    const b = bytes[i + 1];
    const c = bytes[i + 2];
    const n = a * 65536 + (b ?? 0) * 256 + (c ?? 0);
    out += ALPHABET[Math.floor(n / 262144)]! + ALPHABET[Math.floor(n / 4096) % 64]!;
    if (b !== undefined) out += ALPHABET[Math.floor(n / 64) % 64]!;
    if (c !== undefined) out += ALPHABET[n % 64]!;
  }
  return out;
}

export function base64UrlToBytes(text: string): number[] {
  if (text.length % 4 === 1) throw new InkCodecError('Invalid base64url length');
  const out: number[] = [];
  for (let i = 0; i < text.length; i += 4) {
    const chunk = text.slice(i, i + 4);
    let n = 0;
    for (let j = 0; j < 4; j++) {
      const ch = chunk[j];
      const v = ch === undefined ? 0 : LOOKUP.get(ch);
      if (v === undefined) throw new InkCodecError(`Invalid character "${ch}"`);
      n = n * 64 + v;
    }
    out.push(Math.floor(n / 65536));
    if (chunk.length > 2) out.push(Math.floor(n / 256) % 256);
    if (chunk.length > 3) out.push(n % 256);
  }
  return out;
}

export const quantizePosition = (v: number): number => Math.round(v / POSITION_STEP);
export const quantizePressure = (p: number): number =>
  Math.round(Math.min(1, Math.max(0, p)) * PRESSURE_LEVELS);

/** Packs points; dt is stored when at least one point has it (missing dt counts as 0). */
export function encodePoints(points: readonly InkPoint[]): string {
  const withDt = points.some((p) => p.dt !== undefined);
  const out: number[] = [];
  writeVarint(out, withDt ? FLAG_DT : 0);
  writeVarint(out, points.length);
  let px = 0;
  let py = 0;
  let pp = 0;
  for (const pt of points) {
    const x = quantizePosition(pt.x);
    const y = quantizePosition(pt.y);
    const p = quantizePressure(pt.p);
    writeVarint(out, zigzag(x - px));
    writeVarint(out, zigzag(y - py));
    writeVarint(out, zigzag(p - pp));
    if (withDt) writeVarint(out, Math.max(0, Math.round(pt.dt ?? 0)));
    px = x;
    py = y;
    pp = p;
  }
  return bytesToBase64Url(out);
}

/** Unpacks points. Throws {@link InkCodecError} on malformed input; '' decodes to no points. */
export function decodePoints(packed: string): InkPoint[] {
  if (packed === '') return [];
  const r = new Reader(base64UrlToBytes(packed));
  const flags = r.varint();
  const count = r.varint();
  const withDt = flags === FLAG_DT;
  const points: InkPoint[] = [];
  let x = 0;
  let y = 0;
  let p = 0;
  for (let i = 0; i < count; i++) {
    x += unzigzag(r.varint());
    y += unzigzag(r.varint());
    p += unzigzag(r.varint());
    const pt: InkPoint = { x: x * POSITION_STEP, y: y * POSITION_STEP, p: p / PRESSURE_LEVELS };
    if (withDt) pt.dt = r.varint();
    points.push(pt);
  }
  if (!r.done) throw new InkCodecError('Trailing point data');
  return points;
}
